import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { ToastUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { LocalizationUtils } from 'utils'
import { userLoggedOut } from 'core/session/slices/session'
import { runCesPlayer, playerClose } from 'core/staIntegration/slices/staLegacyPlayer'
import {
  getCesDetails,
  getCesLockout,
} from 'modules/activityDetails/CesCstDetails/api/cesCstDetailsService'
import {
  CesCstDetailsState,
  ApiCesCstDetailsResponse,
  ApiCesCstLockoutResponse,
  RunCesParams,
} from './types'

const initialState: CesCstDetailsState = {
  cesCstDetailsData: {},
  cesCstLockoutData: {},
  forceReload: false,
  testId: null,
}

const cesCstDetailsSlice = createSlice({
  name: 'cesCstDetails',
  initialState,
  reducers: {
    cesCstDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCesCstDetailsResponse>>
    ) {
      state.cesCstDetailsData = action.payload
      state.forceReload = false

      const { cesCstDetails } = action.payload.result || {}
      state.testId = cesCstDetails?.details.testId || null
    },
    clearCesCstDetails(state) {
      state.cesCstDetailsData = initialState.cesCstDetailsData
    },
    cesCstLockoutLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCesCstLockoutResponse>>
    ) {
      state.cesCstLockoutData = action.payload
    },
  },
  extraReducers: builder => {
    builder
      .addCase(userLoggedOut, () => {
        return initialState
      })
      .addCase(playerClose, state => {
        state.forceReload = true
        return state
      })
  },
})

const { cesCstDetailsLoaded, clearCesCstDetails, cesCstLockoutLoaded } = cesCstDetailsSlice.actions

export { clearCesCstDetails }
export default cesCstDetailsSlice.reducer

export const loadCesCstDetailsData = (cesId: Guid, resultId?: Guid) => {
  return createApiAction({
    apiCalls: {
      cesCstDetails: () => getCesDetails(cesId, resultId),
    },
    payloadAction: cesCstDetailsLoaded,
  })
}

export const reloadCesCstDetailsData = (cesId: Guid, resultId?: Guid) => {
  return createApiAction({
    apiCalls: {
      cesCstDetails: () => getCesDetails(cesId, resultId),
    },
    payloadAction: cesCstDetailsLoaded,
    shouldShowGlobalLoading: true,
    shouldCallApi: ({ cesCstDetails }) => !cesCstDetails.cesCstDetailsData.isLoading,
    getResultToKeep: ({ cesCstDetails }) => cesCstDetails.cesCstDetailsData.result,
  })
}

export const openCes =
  (scheduleId?: Guid): AppThunk =>
  async (dispatch, getState) => {
    const { testId } = getState().cesCstDetails
    if (!testId) return ToastUtils.error(LocalizationUtils.localizeError('applicationError'))
    dispatch(runCesPlayer(testId, scheduleId))
  }

const runCes =
  (response: ApiMiddlewarePayload<ApiCesCstLockoutResponse>, params: RunCesParams): AppThunk =>
  async dispatch => {
    dispatch(cesCstLockoutLoaded(response))
    const { error, result } = response
    const { scheduleId } = params

    if (!error && !result?.cesCstLockout.isLockedOut) dispatch(openCes(scheduleId))
  }

export const loadCesCstLockoutData = (cesId: Guid, scheduleId?: Guid) => {
  return createApiAction({
    apiCalls: {
      cesCstLockout: () => getCesLockout(cesId),
    },
    payloadAction: cesCstLockoutLoaded,
    nextAction: {
      action: runCes,
      params: { scheduleId },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}
